/**
 * Awards, award lists and the fair-value / final-award records derived from
 * them — the spine of most Help & Tours.
 *
 * Every award names someone from {@link TOUR_EMPLOYEES}, so a tour that
 * searches for an employee on View Awards and then opens the record sees one
 * consistent story rather than two unrelated datasets.
 */

import { TOUR_EMPLOYEES, TourEmployee } from './tour-sample-people';

export interface TourVestingTranche {
  sNo: number;
  vestingDate: string;
  vestingPercentage: number;
  sharesVested: number;
}

export interface TourPerformanceCondition {
  sNo: number;
  performanceMeasureId: number;
  measure: string;
  weight: number;
  threshold: number;
  thresholdFactor: number;
  target: number;
  targetFactor: number;
  superior: number;
  superiorFactor: number;
  minimum: number;
  minimumFactor: number;
  achievementFactor: number;
}

export interface TourAward {
  id: number;
  awardNo: string;
  /**
   * The offer id the View Awards row click puts in the detail URL
   * (`/tenant/award-action-required/{performanceOfferId}`) and the accept /
   * reject call sends as `offerId`. It is NOT the award number, so the detail
   * resolver has to match on it as well.
   */
  performanceOfferId: number;
  awardListId: number;
  employeeId: string;
  employeeName: string;
  planName: string;
  planType: string;
  awardType: string;
  planTypeId: number;
  awardDate: string;
  awardDateDisplay: string;
  performancePeriod: string;
  upcomingVestingDate: string;
  awardedShares: number;
  statusName: string;
  awardStatus: string;
  actionStatus: string;
  respondedOn: string | null;
  expiryDate: string;
  isAcceptAllowedAction: boolean;
  isRejectAllowedAction: boolean;
  lastModifiedOn: string;
  createdBy: string;
  vestingSchedule: TourVestingTranche[];
  /**
   * The performance conditions. `AwardDetailContent` reads
   * `conditions || performanceData` and maps over the result, so this must be
   * the array under both names — a prose string here throws
   * "rawConditions.map is not a function".
   */
  conditions: TourPerformanceCondition[];
  performanceData: TourPerformanceCondition[];
}

const vestingSchedule = (shares: number, year: number): TourVestingTranche[] =>
  [33, 33, 34].map((percentage, index) => ({
    sNo: index + 1,
    vestingDate: `${year + index + 1}-03-15`,
    vestingPercentage: percentage,
    sharesVested: Math.round((shares * percentage) / 100),
  }));

const performanceData = (): TourPerformanceCondition[] => [
  {
    sNo: 1,
    performanceMeasureId: 401,
    measure: 'Total Shareholder Return',
    weight: 50,
    threshold: 5,
    thresholdFactor: 0.5,
    target: 10,
    targetFactor: 1,
    superior: 15,
    superiorFactor: 1.5,
    minimum: 0,
    minimumFactor: 0,
    achievementFactor: 1.15,
  },
  {
    sNo: 2,
    performanceMeasureId: 402,
    measure: 'Return on Equity',
    weight: 30,
    threshold: 8,
    thresholdFactor: 0.5,
    target: 12,
    targetFactor: 1,
    superior: 16,
    superiorFactor: 1.5,
    minimum: 0,
    minimumFactor: 0,
    achievementFactor: 1.1,
  },
  {
    sNo: 3,
    performanceMeasureId: 403,
    measure: 'Earnings Per Share Growth',
    weight: 20,
    threshold: 3,
    thresholdFactor: 0.5,
    target: 6,
    targetFactor: 1,
    superior: 9,
    superiorFactor: 1.5,
    minimum: 0,
    minimumFactor: 0,
    achievementFactor: 1.2,
  },
];

const PLAN_TYPES = [
  { id: 301, name: 'Performance Share Plan', short: 'PSP' },
  { id: 302, name: 'Restricted Share Plan', short: 'RSP' },
  { id: 303, name: 'Employee Share Option Scheme', short: 'ESOS' },
];

interface AwardSeed {
  employee: TourEmployee;
  planIndex: number;
  shares: number;
  statusName: string;
  actionStatus: string;
  year: number;
}

const AWARD_SEEDS: AwardSeed[] = [
  {
    employee: TOUR_EMPLOYEES[0],
    planIndex: 0,
    shares: 4500,
    statusName: 'Action Required',
    actionStatus: 'Action Required',
    year: 2025,
  },
  {
    employee: TOUR_EMPLOYEES[1],
    planIndex: 1,
    shares: 3200,
    statusName: 'Accepted',
    actionStatus: 'Accepted',
    year: 2025,
  },
  {
    employee: TOUR_EMPLOYEES[2],
    planIndex: 0,
    shares: 6000,
    statusName: 'Accepted',
    actionStatus: 'Accepted',
    year: 2024,
  },
  {
    employee: TOUR_EMPLOYEES[3],
    planIndex: 2,
    shares: 2800,
    statusName: 'Action Required',
    actionStatus: 'Action Required',
    year: 2025,
  },
  {
    employee: TOUR_EMPLOYEES[4],
    planIndex: 1,
    shares: 5400,
    statusName: 'Vested',
    actionStatus: 'Completed',
    year: 2023,
  },
  {
    employee: TOUR_EMPLOYEES[5],
    planIndex: 0,
    shares: 7200,
    statusName: 'Accepted',
    actionStatus: 'Accepted',
    year: 2024,
  },
  {
    employee: TOUR_EMPLOYEES[6],
    planIndex: 2,
    shares: 1500,
    statusName: 'Lapsed',
    actionStatus: 'Completed',
    year: 2023,
  },
  {
    employee: TOUR_EMPLOYEES[7],
    planIndex: 1,
    shares: 3900,
    statusName: 'Accepted',
    actionStatus: 'Accepted',
    year: 2025,
  },
];

export const TOUR_AWARDS: readonly TourAward[] = Object.freeze(
  AWARD_SEEDS.map((seed, index) => {
    const plan = PLAN_TYPES[seed.planIndex];
    const awardNo = `AWD-${seed.year}-${String(142 + index * 7).padStart(4, '0')}`;
    const isPending = seed.actionStatus === 'Action Required';
    const conditions = performanceData();

    return {
      id: 5000 + index,
      awardNo,
      performanceOfferId: 70000 + index,
      awardListId: 840 + index,
      employeeId: seed.employee.employeeId,
      employeeName: seed.employee.fullName,
      planName: `Northwind ${plan.name} ${seed.year}`,
      planType: plan.name,
      awardType: plan.name,
      planTypeId: plan.id,
      awardDate: `${seed.year}-03-15`,
      awardDateDisplay: `15 Mar ${seed.year}`,
      performancePeriod: `01 Jan ${seed.year} - 31 Dec ${seed.year + 2}`,
      upcomingVestingDate: `15 Mar ${seed.year + 1}`,
      awardedShares: seed.shares,
      statusName: seed.statusName,
      awardStatus: seed.statusName,
      actionStatus: seed.actionStatus,
      respondedOn: isPending ? null : `${seed.year}-03-28`,
      expiryDate: `${seed.year}-04-30`,
      isAcceptAllowedAction: isPending,
      isRejectAllowedAction: isPending,
      lastModifiedOn: `${seed.year}-03-20T10:15:00Z`,
      createdBy: 'Sarah Lim',
      vestingSchedule: vestingSchedule(seed.shares, seed.year),
      conditions,
      performanceData: conditions,
    };
  })
);

/** The award the interactive award tours drive — it still awaits a response. */
export const TOUR_PRIMARY_AWARD = TOUR_AWARDS[0];

/** Awards still awaiting the participant's accept / reject. */
export const TOUR_ACTION_REQUIRED_AWARDS = TOUR_AWARDS.filter(
  award => award.actionStatus === 'Action Required'
);

/* -------------------------------------------------------------------------- */
/* Award lists — the uploaded batches Manage Award works through              */
/* -------------------------------------------------------------------------- */

export interface TourAwardList {
  id: number;
  awardListId: number;
  fileId: string;
  fileName: string;
  awardNo: string;
  awardType: string;
  planType: string;
  uploadDate: string;
  approvedDate: string | null;
  status: string;
  statusName: string;
  letterStatus: string;
  awardLetter: string;
  noOfRecipients: number;
  totalShares: number;
  uploadedBy: string;
  lastModifiedOn: string;
}

export const TOUR_AWARD_LISTS: readonly TourAwardList[] = Object.freeze([
  {
    id: 840,
    awardListId: 840,
    fileId: 'TOUR-LIST-0840',
    fileName: 'PSP-2025-Grant-Batch-01.xlsx',
    awardNo: 'AWD-2025-0142',
    awardType: 'Performance Share Plan',
    planType: 'Performance Share Plan',
    uploadDate: '2025-03-10T09:12:00Z',
    approvedDate: '2025-03-12T16:40:00Z',
    status: 'Approved',
    statusName: 'Approved',
    letterStatus: 'Generated',
    awardLetter: 'Generated',
    noOfRecipients: 48,
    totalShares: 186400,
    uploadedBy: 'Sarah Lim',
    lastModifiedOn: '2025-03-12T16:40:00Z',
  },
  {
    id: 841,
    awardListId: 841,
    fileId: 'TOUR-LIST-0841',
    fileName: 'RSP-2025-Grant-Batch-02.xlsx',
    awardNo: 'AWD-2025-0149',
    awardType: 'Restricted Share Plan',
    planType: 'Restricted Share Plan',
    uploadDate: '2025-04-02T11:05:00Z',
    approvedDate: null,
    status: 'Pending Checker Approval',
    statusName: 'Pending Checker Approval',
    letterStatus: 'Not Generated',
    awardLetter: 'Not Generated',
    noOfRecipients: 26,
    totalShares: 82300,
    uploadedBy: 'Arun Kumar',
    lastModifiedOn: '2025-04-02T11:05:00Z',
  },
  {
    id: 842,
    awardListId: 842,
    fileId: 'TOUR-LIST-0842',
    fileName: 'ESOS-2025-Grant-Batch-01.xlsx',
    awardNo: 'AWD-2025-0156',
    awardType: 'Employee Share Option Scheme',
    planType: 'Employee Share Option Scheme',
    uploadDate: '2025-05-19T14:22:00Z',
    approvedDate: null,
    status: 'Draft',
    statusName: 'Draft',
    letterStatus: 'Not Generated',
    awardLetter: 'Not Generated',
    noOfRecipients: 12,
    totalShares: 31500,
    uploadedBy: 'Daniel Tan',
    lastModifiedOn: '2025-05-19T14:22:00Z',
  },
]);

export const TOUR_PRIMARY_AWARD_LIST = TOUR_AWARD_LISTS[0];

/** One recipient row inside an uploaded award list. */
export interface TourAwardListRecipient {
  id: number;
  sNo: number;
  employeeId: string;
  employeeName: string;
  email: string;
  department: string;
  designation: string;
  awardNo: string;
  planType: string;
  awardedShares: number;
  grantDate: string;
  status: string;
  isExcluded: boolean;
  remarks: string | null;
  /** The generate-letter wizard's suppression toggle (`suppressField`). */
  isEmailSuppress: boolean;
  /** Identifier its suppression update is keyed by (`buildUpdate`). */
  offerId: number;
}

export const TOUR_AWARD_LIST_RECIPIENTS: readonly TourAwardListRecipient[] =
  Object.freeze(
    TOUR_EMPLOYEES.map((employee, index) => ({
      id: 6100 + index,
      sNo: index + 1,
      employeeId: employee.employeeId,
      employeeName: employee.fullName,
      email: employee.email,
      department: employee.department,
      designation: employee.designation,
      awardNo: `AWD-2025-${String(142 + index * 7).padStart(4, '0')}`,
      planType: 'Performance Share Plan',
      awardedShares: 2500 + index * 450,
      grantDate: '2025-03-15',
      status: index === 6 ? 'Excluded' : 'Included',
      isExcluded: index === 6,
      remarks: index === 6 ? 'Left the company before the grant date' : null,
      isEmailSuppress: false,
      offerId: 70000 + index,
    }))
  );

/* -------------------------------------------------------------------------- */
/* Fair value                                                                 */
/* -------------------------------------------------------------------------- */

export interface TourFairValueRow {
  id: number;
  awardNo: string;
  planTypeCode: string;
  planType: string;
  awardYear: number;
  tranche: number;
  performanceMeasureID: number;
  measure: string;
  isWeightedAvg: boolean;
  performanceConditionFairValue: number;
  performanceConditionExpectedFactor: number;
  grantDate: string;
  status: string;
}

export const TOUR_FAIR_VALUES: readonly TourFairValueRow[] = Object.freeze(
  TOUR_AWARDS.slice(0, 5).flatMap((award, awardIndex) =>
    [1, 2].map(tranche => ({
      id: 7200 + awardIndex * 10 + tranche,
      awardNo: award.awardNo,
      planTypeCode: ['PSP', 'RSP', 'ESOS'][awardIndex % 3],
      planType: award.planType,
      awardYear: Number(award.awardDate.slice(0, 4)),
      tranche,
      performanceMeasureID: 400 + tranche,
      measure: tranche === 1 ? 'Total Shareholder Return' : 'Return on Equity',
      isWeightedAvg: tranche === 1,
      performanceConditionFairValue: Number(
        (3.85 + awardIndex * 0.12).toFixed(2)
      ),
      performanceConditionExpectedFactor: Number(
        (1 + tranche * 0.05).toFixed(2)
      ),
      grantDate: award.awardDate,
      status: 'Active',
    }))
  )
);

/* -------------------------------------------------------------------------- */
/* Final awards                                                               */
/* -------------------------------------------------------------------------- */

export interface TourFinalAward {
  id: number;
  awardNo: string;
  employeeId: string;
  employeeName: string;
  planName: string;
  planType: string;
  performancePeriod: string;
  contingentShares: number;
  achievementFactor: number;
  finalShares: number;
  vestingDate: string;
  status: string;
  statusName: string;
  lastModifiedOn: string;
  computedBy: string;
}

export const TOUR_FINAL_AWARDS: readonly TourFinalAward[] = Object.freeze(
  TOUR_AWARDS.slice(0, 6).map((award, index) => {
    const factor = [1.15, 1.1, 0.95, 1.2, 1, 1.05][index];
    return {
      id: 8300 + index,
      awardNo: award.awardNo,
      employeeId: award.employeeId,
      employeeName: award.employeeName,
      planName: award.planName,
      planType: award.planType,
      performancePeriod: award.performancePeriod,
      contingentShares: award.awardedShares,
      achievementFactor: factor,
      finalShares: Math.round(award.awardedShares * factor),
      vestingDate: award.upcomingVestingDate,
      status: index < 2 ? 'Pending Approval' : 'Approved',
      statusName: index < 2 ? 'Pending Approval' : 'Approved',
      lastModifiedOn: '2025-08-14T09:30:00Z',
      computedBy: 'Sarah Lim',
    };
  })
);

export const TOUR_PRIMARY_FINAL_AWARD = TOUR_FINAL_AWARDS[0];

/** The letter every sample final award is configured with. */
const TOUR_FINAL_AWARD_LETTER_NAME = 'Final Award Vesting Letter';

/** `FinalAwardPendingApproval` — what the checker list holds by default. */
export const TOUR_FINAL_AWARD_PENDING_APPROVAL_STATUS_ID = 1213;

/**
 * A final award as `GET /finalaward/approvallist` returns it
 * (`FinalAwardListItemDto`). The Administration Hub's Final Award table
 * reads these field names, not the sample record's own.
 */
export const toTourFinalAwardApprovalRow = (award: TourFinalAward) => {
  const list = TOUR_AWARD_LISTS.find(item => item.awardNo === award.awardNo);
  return {
    awardNo: award.awardNo,
    awardType: award.planType,
    awardeeCount: list?.noOfRecipients ?? 1,
    awardLetterName: TOUR_FINAL_AWARD_LETTER_NAME,
    awardLetterId: 4102,
    awardListFileName: list?.fileName ?? null,
    statusId: TOUR_FINAL_AWARD_PENDING_APPROVAL_STATUS_ID,
    statusDisplay: 'Pending Final Award Approval',
    makerName: award.computedBy,
    submittedOn: award.lastModifiedOn,
  };
};

/** Email letters need no cover letter, which keeps the demo config minimal. */
const TOUR_FINAL_AWARD_LETTER = {
  awardLetterTypeId: 1,
  awardLetterTypeName: 'Email',
  awardLetterId: 4102,
  awardLetterName: TOUR_FINAL_AWARD_LETTER_NAME,
  coverLetterId: null,
  coverLetterName: null,
};

/**
 * The achieved values the sample maker entered before sending each pending
 * award for approval, by performance measure. Chosen so the computed total
 * matches the award's own achievement factor (1.15 and 1.10).
 */
const TOUR_SUBMITTED_ACHIEVEMENTS = new Map<string, Record<number, number>>([
  ['AWD-2025-0142', { 401: 11, 402: 16.5, 403: 7 }],
  ['AWD-2025-0149', { 401: 12, 402: 13, 403: 9.5 }],
]);

/**
 * `achievementDetails` for an award already sent for approval: every condition
 * with its achieved value and factor, the total and "condition met". This is
 * what the checker's review page renders; without it the page reads
 * "No conditions added." and a total of 0.
 */
export const getTourSubmittedAchievementDetails = (awardNo: string) => {
  const achieved =
    TOUR_SUBMITTED_ACHIEVEMENTS.get(awardNo) ??
    TOUR_SUBMITTED_ACHIEVEMENTS.get(TOUR_PRIMARY_FINAL_AWARD.awardNo)!;
  const { conditions: computed, totalAchievementFactor } =
    computeTourAchievementFactors(achieved);

  return {
    calculationMethod: 'System',
    isConditionMet: true,
    totalAchievementFactor,
    performancePeriodEndDate: '2027-12-31T00:00:00',
    conditions: TOUR_PRIMARY_AWARD.performanceData.map((condition, index) => ({
      id: condition.performanceMeasureId,
      measureName: condition.measure,
      weight: condition.weight,
      minimum: condition.minimum,
      minimumFactor: condition.minimumFactor,
      threshold: condition.threshold,
      thresholdFactor: condition.thresholdFactor,
      target: condition.target,
      targetFactor: condition.targetFactor,
      superior: condition.superior,
      superiorFactor: condition.superiorFactor,
      achievement: computed[index].achievement,
      achievementFactor: computed[index].calculatedFactor,
    })),
  };
};

/**
 * The Final Award summary the wizard hydrates itself from.
 *
 * The Award Letter stage seeds its form from this response, and Proceed there
 * is gated on `validateLetterStep` — which needs an award letter type and
 * template (and a cover letter for anything other than Email). Supplying them
 * here means the tour reaches the Summary stage without a production prefill.
 */
export const getTourFinalAwardSummary = (
  awardNo?: string,
  { submitted = false }: { submitted?: boolean } = {}
) => {
  const award =
    TOUR_FINAL_AWARDS.find(item => item.awardNo === awardNo) ??
    TOUR_PRIMARY_FINAL_AWARD;

  if (submitted) {
    return {
      ...award,
      ...TOUR_FINAL_AWARD_LETTER,
      achievementDetails: getTourSubmittedAchievementDetails(award.awardNo),
      adjustmentDetails: null,
    };
  }

  return {
    ...award,
    ...TOUR_FINAL_AWARD_LETTER,
    achievementDetails: {
      totalAchievementFactor: null,
      isConditionMet: null,
      conditions: [],
    },
  };
};

/**
 * The adjustment summary Proceed checks before leaving Stage 2. Non-zero
 * exception counts block the stage, so the demo award reports none.
 */
export const getTourFinalAwardAdjustmentSummary = (awardNo?: string) => ({
  ...getTourFinalAwardSummary(awardNo),
  serviceDayExceptionCount: 0,
  finalAdjustmentExceptionCount: 0,
  adjustments: [],
  items: [],
  totalCount: 0,
});

/**
 * What `POST /finalaward/{awardNo}/compute` answers with.
 *
 * The stage feeds the computed values back into its table, so an
 * acknowledgement is not enough — it needs a factor per condition plus the
 * weighted total. The maths is done here rather than canned, so the result
 * follows whatever achieved values the user typed.
 */
export const computeTourAchievementFactors = (
  achievements: Record<string, number>
) => {
  const conditions = TOUR_PRIMARY_AWARD.performanceData.map(condition => {
    const achieved = Number(achievements[condition.performanceMeasureId] ?? 0);

    // Walk the bands from the top: the first one the achievement reaches wins.
    let factor = 0;
    if (achieved >= condition.superior) factor = condition.superiorFactor;
    else if (achieved >= condition.target) factor = condition.targetFactor;
    else if (achieved >= condition.threshold)
      factor = condition.thresholdFactor;
    else if (achieved >= condition.minimum) factor = condition.minimumFactor;

    return {
      conditionId: condition.performanceMeasureId,
      achievement: achieved,
      calculatedFactor: factor,
    };
  });

  // The overall factor is the per-condition factors weighted by their weights.
  const totalAchievementFactor = Number(
    conditions
      .reduce((total, condition, index) => {
        const weight = TOUR_PRIMARY_AWARD.performanceData[index].weight;
        return total + condition.calculatedFactor * (weight / 100);
      }, 0)
      .toFixed(4)
  );

  return { conditions, totalAchievementFactor };
};

/**
 * The performance conditions as the Final Award wizard's Achievement Factor
 * stage reads them.
 *
 * This endpoint answers with an OBJECT wrapping the rows — the page reads
 * `res.conditions`, `res.isConditionMet` and `res.totalAchievementFactor`, and
 * uses `measureName` rather than the `measure` the award detail view expects.
 * Returning the award's bare `performanceData` array here leaves the stage
 * showing "No conditions added."
 */
export const getTourFinalAwardConditions = (awardNo?: string) => {
  const award =
    TOUR_FINAL_AWARDS.find(item => item.awardNo === awardNo) ??
    TOUR_PRIMARY_FINAL_AWARD;

  return {
    awardNo: award.awardNo,
    performancePeriodEndDate: '2027-12-31T00:00:00',
    // Left unset so the stage opens ready to be computed, which is what the
    // tour walks the user through.
    isConditionMet: null,
    totalAchievementFactor: null,
    conditions: TOUR_PRIMARY_AWARD.performanceData.map(condition => ({
      id: condition.performanceMeasureId,
      measureName: condition.measure,
      weight: condition.weight,
      minimum: condition.minimum,
      minimumFactor: condition.minimumFactor,
      threshold: condition.threshold,
      thresholdFactor: condition.thresholdFactor,
      target: condition.target,
      targetFactor: condition.targetFactor,
      superior: condition.superior,
      superiorFactor: condition.superiorFactor,
      achievement: null,
      achievementFactor: null,
    })),
  };
};
